import { useContext, useEffect, useState } from 'react';

import { ProductDataStore } from 'src/modules/designStudio/helpers/productSelection/ProductDataStore';
import { ProductsRouter } from 'src/modules/designStudio/helpers/productSelection/ProductsRouter';
import type { EndpointData } from 'src/modules/designStudio/helpers/productSelection/endpointTypes';
import { request } from 'src/shared/helpers/request';
import { logError } from 'src/shared/telemetry/logger';

import { context } from '../ModeProvider';

const DATA_URL =
  'https://products.hover.to/hover_models/provider_products.json';

const DESIGN_PRO_GENERIC_PROVIDER_ID = 8;
const GENERIC_PROVIDER_ID = 8850;

const separateProductData = (
  data: EndpointData,
): { realProductsData: EndpointData; genericProductsData: EndpointData } => ({
  realProductsData: data.filter(
    /**
     * We never want to show DESIGN_PRO_GENERIC_PROVIDER_ID products, these products should only be shown in design-pro.
     */
    provider =>
      provider.id !== GENERIC_PROVIDER_ID &&
      provider.id !== DESIGN_PRO_GENERIC_PROVIDER_ID,
  ),
  genericProductsData: data.filter(
    provider => provider.id === GENERIC_PROVIDER_ID,
  ),
});

const fetchProductData = async (): Promise<EndpointData> => {
  try {
    const response = await request(DATA_URL);
    return await response.json();
  } catch (e) {
    logError(e as Error);
  }
  return [];
};

export const useProductsRouter = ({
  isPrintDebugOn,
}: {
  isPrintDebugOn: boolean;
}) => {
  const { mode, partner, hasPartnerIdBeenFetched } = useContext(context);

  const [hasProductsDataBeenFetched, setHasProductsDataBeenFetched] =
    useState(false);

  const [productsRouter, setProductsRouter] = useState<
    ProductsRouter | undefined
  >();

  useEffect(() => {
    if (!hasProductsDataBeenFetched && hasPartnerIdBeenFetched) {
      if (isPrintDebugOn) {
        // eslint-disable-next-line no-console
        console.info(`Fetching product data...`);
      }
      setHasProductsDataBeenFetched(true);
      void fetchProductData().then(data => {
        if (isPrintDebugOn) {
          // eslint-disable-next-line no-console
          console.info(`Product data fetched.`);
        }
        const { realProductsData, genericProductsData } =
          separateProductData(data);
        setProductsRouter(
          new ProductsRouter(
            new ProductDataStore(realProductsData),
            new ProductDataStore(genericProductsData),
            partner,
            mode,
            isPrintDebugOn,
          ),
        );
      });
    }
  }, [
    hasProductsDataBeenFetched,
    partner,
    mode,
    isPrintDebugOn,
    hasPartnerIdBeenFetched,
  ]);

  useEffect(() => {
    if (productsRouter && productsRouter.partner !== partner) {
      if (isPrintDebugOn) {
        // eslint-disable-next-line no-console
        console.info(`Changing productsRouter.partner to ${partner}`);
      }
      productsRouter.partner = partner;
    }
  }, [productsRouter, partner, isPrintDebugOn]);

  return {
    productsRouter,
  };
};
